import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  catchError,
  concatMap,
  exhaustMap,
  filter,
  map,
  switchMap,
  withLatestFrom,
  of,
  interval,
  startWith,
  takeUntil,
  Observable,
} from 'rxjs';
import * as CampaignActions from './campaign.actions';
import { CampaignState } from './campaign.state';
import { CommunicationDetail, SmsCommunicationDetail } from '../model/campaign.interface';
import {
  selectStandardSorting,
  selectStandardPage,
  selectStandardPageSize,
  selectArchivedSorting,
  selectArchivedPage,
  selectArchivedPageSize,
  selectSearch,
  selectUsubImportState,
  selectUnsubscribeState,
  selectStandardStatusFilter,
  selectArchivedStatusFilter,
  selectCommunicationHistoryChannel,
  selectCommunicationHistoryContact,
  selectCommunicationHistoryFilters,
  selectCommunicationHistoryCursor,
  selectCommunicationHistorySortOrder,
  selectActiveTab,
} from './campaign.selectors';
import { CampaignService } from '../services/campaign.service';
import { ImportOptoutsModalService } from '../modals/unsubscribe/email-suppression-modal/import-opt-outs-wizard-modal/import-optouts-modal.service';
import { UnsubscribeService } from '../services/unsubscribe.service';

@Injectable()
export class CampaignEffects {
  constructor(
    private actions$: Actions,
    private campaignService: CampaignService,
    private unsubscribeService: UnsubscribeService,
    private importOptoutsModalService: ImportOptoutsModalService,
    private store: Store<CampaignState>
  ) {}

  private getFileNameWithId(fileName: string, fileId: number): string {
    const fileExtensionMatch = fileName.match(/(\.[^.]+)$/);
    const extension = fileExtensionMatch ? fileExtensionMatch[0] : '';
    const fileNameWithoutExt = extension ? fileName.slice(0, -extension.length) : fileName;
    return `${fileNameWithoutExt}_${fileId}${extension}`;
  }

  loadStandardCampaigns$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CampaignActions.loadStandardCampaigns),
      withLatestFrom(
        this.store.select(selectStandardSorting),
        this.store.select(selectSearch),
        this.store.select(selectStandardPageSize),
        this.store.select(selectStandardStatusFilter)
      ),
      switchMap(([action, sorting, search, pageSize, statusFilter]) => {
        // If all statuses are selected (6 total), send empty string to indicate no filter
        const filterToSend = statusFilter.length === 6 ? '' : statusFilter;
        return this.campaignService
          .getAllCampaigns(action.page, sorting.sortBy, sorting.sortOrder, pageSize, search?.trim(), filterToSend)
          .pipe(
            map((result: any) => {
              if (result && result.data) {
                return CampaignActions.loadStandardCampaignsSuccess({
                  campaigns: result.data.records || [],
                  filteredCount: result.data.filteredCount || 0,
                  totalArchivedCount: result.data.totalArchivedCount ?? 0,
                  totalActiveCount: result.data.totalActiveCount ?? 0,
                  page: action.page,
                  enableSecureDriveImport: result.data.enableSecureDriveImport ?? false,
                });
              }
              return CampaignActions.loadStandardCampaignsSuccess({
                campaigns: [],
                filteredCount: 0,
                totalArchivedCount: 0,
                totalActiveCount: 0,
                page: action.page,
                enableSecureDriveImport: false,
              });
            }),
            catchError((error) =>
              of(CampaignActions.loadStandardCampaignsFailure({ error: error.message || 'Something went wrong.' }))
            )
          );
      }
      )
    )
  );

  loadArchivedCampaigns$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CampaignActions.loadArchivedCampaigns),
      withLatestFrom(
        this.store.select(selectArchivedSorting),
        this.store.select(selectSearch),
        this.store.select(selectArchivedPageSize),
        this.store.select(selectArchivedStatusFilter)
      ),
      switchMap(([action, sorting, search, pageSize, statusFilter]) => {
        // If all statuses are selected (6 total), send empty string to indicate no filter
        const filterToSend = statusFilter.length === 6 ? '' : statusFilter;
        return this.campaignService
          .getAllArchivedCampaigns(action.page, sorting.sortBy, sorting.sortOrder, pageSize, search?.trim(), filterToSend)
          .pipe(
            map((result: any) => {
              if (result && result.data) {
                return CampaignActions.loadArchivedCampaignsSuccess({
                  campaigns: result.data.records || [],
                  totalActiveCount: result.data.totalActiveCount ?? 0,
                  totalArchivedCount: result.data.totalArchivedCount ?? 0,
                  filteredCount: result.data.filteredCount || 0,
                  page: action.page,
                });
              }
              return CampaignActions.loadArchivedCampaignsSuccess({
                campaigns: [],
                totalActiveCount: 0,
                totalArchivedCount: 0,
                filteredCount: 0,
                page: action.page,
              });
            }),
            catchError((error) =>
              of(CampaignActions.loadArchivedCampaignsFailure({ error: error.message || 'Something went wrong.' }))
            )
          );
      }
      )
    )
  );

  startPolling$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CampaignActions.startPolling),
      switchMap(({ ids }) =>
        interval(10000).pipe(
          startWith(0),
          takeUntil(this.actions$.pipe(ofType(CampaignActions.stopPolling))),
          switchMap(() =>
            this.campaignService.getListUpdatedStatus(ids).pipe(
              map((result: any) => {
                if (result && result.data) {
                  return CampaignActions.pollSuccess({ statuses: result.data });
                }
                return CampaignActions.pollSuccess({ statuses: [] });
              }),
              catchError((error) =>
                of(CampaignActions.pollFailure({ error: error.message || 'Polling error' }))
              )
            )
          )
        )
      )
    )
  );

  // After standard campaigns are loaded successfully, start polling for their statuses
  // Guard: only start polling when the standard campaigns tab (0) is active.
  // If the user has navigated away (e.g. to the Records tab), a late-arriving
  // loadStandardCampaignsSuccess must not restart polling.
  startPollingAfterLoad$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CampaignActions.loadStandardCampaignsSuccess),
      withLatestFrom(this.store.select(selectActiveTab)),
      switchMap(([{ campaigns }, activeTab]) => {
        if (activeTab !== 0) {
          return [CampaignActions.stopPolling()];
        }
        if (campaigns && campaigns.length > 0) {
          const idsString = campaigns.map((r: any) => r.id).join(', ');
          return [
            CampaignActions.stopPolling(),
            CampaignActions.startPolling({ ids: idsString }),
          ];
        }
        return [CampaignActions.stopPolling()];
      })
    )
  );

  // Reload standard campaigns after a copy succeeds
  reloadAfterCopy$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CampaignActions.copyCampaignSuccess),
      withLatestFrom(this.store.select(selectStandardPage)),
      map(([_, page]) => CampaignActions.loadStandardCampaigns({ page }))
    )
  );

  // Reload both lists after archive succeeds
  reloadAfterArchive$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CampaignActions.archiveCampaignSuccess),
      switchMap(({ adjustedPage }) => [
        CampaignActions.loadStandardCampaigns({ page: adjustedPage }),
      ])
    )
  );

  // Reload standard campaigns after status change (start/stop/resume)
  reloadAfterStatusChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CampaignActions.campaignStatusChanged),
      withLatestFrom(this.store.select(selectStandardPage)),
      map(([_, page]) => CampaignActions.loadStandardCampaigns({ page }))
    )
  );

  // ─── Phase 2: Load Campaign Detail ──────────────────────────────────────────

  loadCampaignDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CampaignActions.loadCampaignDetail),
      switchMap(({ campaignId }) =>
        this.campaignService.getCampaignById(campaignId).pipe(
          map((result: any) => {
            if (result && result.data && result.isActionSuccess) {
              return CampaignActions.loadCampaignDetailSuccess({
                campaignDetails: result.data,
                campaignId,
              });
            }
            return CampaignActions.loadCampaignDetailFailure({
              error: 'Campaign data not found or action unsuccessful',
            });
          }),
          catchError((error) =>
            of(CampaignActions.loadCampaignDetailFailure({
              error: error.message || 'Something went wrong.',
            }))
          )
        )
      )
    )
  );

  downloadAuditList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CampaignActions.downloadAuditList),
      exhaustMap(({ query }) =>
        this.campaignService.startFileDownload(query).pipe(
          map(() => CampaignActions.downloadAuditListSuccess()),
          catchError((error) =>
            of(CampaignActions.downloadAuditListFailure({
              error: error?.message || 'Failed to download.',
            }))
          )
        )
      )
    )
  );

  loadAuditHistory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CampaignActions.loadAuditHistory),
      switchMap(({ campaignId, pageNumber, limit }) => {
        const request$ = campaignId
          ? this.campaignService.getAuditHistory(campaignId, pageNumber, limit)
          : this.campaignService.getGeneralAuditHistory(pageNumber, limit);

        return request$.pipe(
          map((result: any) => {
            // Both branches yield AuditHistoryRecord.auditHistory as an already-parsed
            // object: the campaign-specific API parses its stored JSON string server-side,
            // while the general-audit API (patient-file-upload/audit-history) parses it there too
            // and row.auditHistory arrives pre-parsed — no JSON.parse needed here either way.
            const records = campaignId
              ? (result?.data?.records ?? [])
              : (result?.data ?? []).map((row: any) => ({
                  id: String(row.id),
                  campaignId: '',
                  status: '',
                  auditHistory: row.auditHistory ?? null,
                  createdById: row.createdBy?.id ?? '',
                  createdDateTimeUtc: row.occurredAt,
                  createdByUserName: row.createdBy?.displayName || row.createdBy?.userName || '',
                  sourceCampaignName: '',
                }));
            const totalCount = campaignId
              ? (result?.data?.totalCount ?? 0)
              : (result?.count ?? 0);
            return CampaignActions.loadAuditHistorySuccess({
              records,
              totalCount,
              pageNumber,
              limit,
            });
          }),
          catchError((error) =>
            of(CampaignActions.loadAuditHistoryFailure({
              error: error?.message || 'Failed to load audit history.',
            }))
          )
        );
      })
    )
  );
  searchCommunicationHistory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CampaignActions.searchCommunicationHistory),
      switchMap(({ channel, contact, campaignName, listName, sortOrder, pageSize }) =>
        this.campaignService
          .searchCommunicationHistory({
            channel,
            contact,
            campaignName: campaignName || '',
            listName: listName || '',
            sortOrder: sortOrder ?? 'DESC',
            limit: pageSize ?? 10,
          })
          .pipe(
            map((result: any) =>
              CampaignActions.searchCommunicationHistorySuccess({
                records: result?.data?.records ?? [],
                matchingRecordCount: result?.data?.matchingRecordCount ?? 0,
                nextCursor: result?.data?.nextCursor ?? null,
                isFirstPage: true,
              })
            ),
            catchError((error) =>
              of(
                CampaignActions.searchCommunicationHistoryFailure({
                  error: error?.error?.Error || error?.message || 'Failed to search communication history.',
                })
              )
            )
          )
      )
    )
  );

  loadMoreCommunicationHistory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CampaignActions.loadMoreCommunicationHistory),
      withLatestFrom(
        this.store.select(selectCommunicationHistoryChannel),
        this.store.select(selectCommunicationHistoryContact),
        this.store.select(selectCommunicationHistoryFilters),
        this.store.select(selectCommunicationHistoryCursor),
        this.store.select(selectCommunicationHistorySortOrder),
      ),
      switchMap(([{ pageSize }, channel, contact, filters, cursor, sortOrder]) =>
        this.campaignService
          .searchCommunicationHistory({
            channel,
            contact,
            campaignName: filters.campaignName,
            listName: filters.listName,
            cursor: cursor ?? undefined,
            sortOrder: sortOrder ?? 'DESC',
            limit: pageSize ?? 10,
          })
          .pipe(
            map((result: any) =>
              CampaignActions.searchCommunicationHistorySuccess({
                records: result?.data?.records ?? [],
                matchingRecordCount: result?.data?.matchingRecordCount ?? 0,
                nextCursor: result?.data?.nextCursor ?? null,
                isFirstPage: false,
              })
            ),
            catchError((error) =>
              of(
                CampaignActions.searchCommunicationHistoryFailure({
                  error: error?.error?.Error || error?.message || 'Failed to load more communication history.',
                })
              )
            )
          )
      )
    )
  );

  loadCommunicationDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CampaignActions.loadCommunicationDetail),
      switchMap(({ campaignTaskId, channel }) => {
        const detail$: Observable<CommunicationDetail | SmsCommunicationDetail> = channel === 'SMS'
          ? this.campaignService.getSmsCommunicationDetail(campaignTaskId)
          : this.campaignService.getCommunicationDetail(campaignTaskId);
        return detail$.pipe(
          map((result: any) =>
            CampaignActions.loadCommunicationDetailSuccess({
              detail: result?.data ?? result,
            })
          ),
          catchError((error) =>
            of(
              CampaignActions.loadCommunicationDetailFailure({
                error: error?.error?.Error || error?.message || 'Failed to load communication detail.',
              })
            )
          )
        );
      })
    )
  );

  autoSelectFirstRecord$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CampaignActions.searchCommunicationHistorySuccess),
      filter(({ isFirstPage, records }) => isFirstPage && records.length > 0),
      withLatestFrom(this.store.select(selectCommunicationHistoryChannel)),
      concatMap(([{ records }, channel]) =>
        of(
          CampaignActions.selectCommunicationHistoryRecord({ record: records[0] }),
          CampaignActions.loadCommunicationDetail({ campaignTaskId: records[0].campaignTaskId, channel })
        )
      )
    )
  );

  // ─── Phase 4: Unsubscribe Management Effects ─────────────────────────────

  loadUnsubscribeList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CampaignActions.loadUnsubscribeList),
      withLatestFrom(this.store.select(selectUnsubscribeState)),
      switchMap(([action, unsubscribe]) => {
        const isEmail = action.listType === 'email';
        const sorting = isEmail ? unsubscribe.emailSorting : unsubscribe.smsSorting;
        const search = (isEmail ? unsubscribe.emailSearch : unsubscribe.smsSearch)?.trim();
        const selectedDomain = isEmail ? unsubscribe.emailSelectedDomain : null;
        const startDate = isEmail ? unsubscribe.emailStartDate : unsubscribe.smsStartDate;
        const endDate = isEmail ? unsubscribe.emailEndDate : unsubscribe.smsEndDate;

        return this.unsubscribeService
          .getUnsubscribers(
            action.page,
            sorting.sortBy,
            sorting.sortOrder,
            10,
            search,
            action.listType,
            selectedDomain,
            startDate,
            endDate,
          )
          .pipe(
            map((result: any) => {
              const records = result?.data?.records || [];
              const totalCount = result?.data?.totalCount || 0;
              const page = action.listType === 'sms' && result?.data?.pageNumber
                ? result.data.pageNumber
                : action.page;

              return CampaignActions.loadUnsubscribeListSuccess({
                listType: action.listType,
                records,
                totalCount,
                page,
              });
            }),
            catchError((error) =>
              of(CampaignActions.loadUnsubscribeListFailure({
                error: error?.message || 'Unable to load unsubscribe records.',
              }))
            )
          );
      })
    )
  );

  loadUnsubscribeDomains$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CampaignActions.loadUnsubscribeDomains),
      switchMap(() =>
        this.unsubscribeService.getDomainList().pipe(
          map((result: any) => CampaignActions.loadUnsubscribeDomainsSuccess({
            domains: result?.data?.domains || [],
          })),
          catchError((error) =>
            of(CampaignActions.loadUnsubscribeDomainsFailure({
              error: error?.message || 'Unable to load domain list.',
            }))
          )
        )
      )
    )
  );

  loadUnsubscribeActivePhone$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CampaignActions.loadUnsubscribeActivePhone),
      switchMap(() =>
        this.unsubscribeService.getActivePhone().pipe(
          map(({ senderPhoneNumber }) => CampaignActions.loadUnsubscribeActivePhoneSuccess({
            phoneNumber: senderPhoneNumber || '',
          })),
          catchError((error) =>
            of(CampaignActions.loadUnsubscribeActivePhoneFailure({
              error: error?.message || 'Unable to load active phone number.',
            }))
          )
        )
      )
    )
  );

  exportUnsubscribeList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CampaignActions.exportUnsubscribeList),
      exhaustMap(({ query }) =>
        this.unsubscribeService.startUnsubscribeDownload(query).pipe(
          map(() => CampaignActions.exportUnsubscribeListSuccess()),
          catchError((error) =>
            of(CampaignActions.exportUnsubscribeListFailure({
              error: error?.message || 'Unable to export unsubscribe records.',
            }))
          )
        )
      )
    )
  );

  submitUsubManualOptOuts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CampaignActions.submitUsubManualOptOuts),
      exhaustMap(({ payload, listType }) =>
        this.unsubscribeService.addOptOuts(payload).pipe(
          switchMap((result: any) => {
            if (result?.isSuccess || result?.isActionSuccess) {
              return [
                CampaignActions.submitUsubManualOptOutsSuccess({
                  message: listType === 'sms' ?
                  (result?.actualValidRows && payload.phoneNumbers.length - result?.actualValidRows > 0 ? `${result?.actualValidRows} new Opt-outs added Successfully. ${payload.phoneNumbers.length - result?.actualValidRows} Opt-outs already exist.` : ( result?.actualValidRows > 0 ? `${result?.actualValidRows} new Opt-outs added Successfully.` : 'No new Opt-outs added.'))
                  : ( result?.actualValidRows && payload.emails.length - result?.actualValidRows > 0 ? `${result?.actualValidRows} new Unsubscribes added Successfully. ${payload.emails.length - result?.actualValidRows} Unsubscribes already exist.` : ( result?.actualValidRows > 0 ? `${result?.actualValidRows} new Unsubscribes added Successfully.` : 'No new Unsubscribes added.')),
                  listType,
                  actualValidRows: result?.actualValidRows ?? null,
                }),
              ];
            }
            return [CampaignActions.submitUsubManualOptOutsFailure({ error: 'Unable to add opt-outs.' })];
          }),
          catchError((error) =>
            of(CampaignActions.submitUsubManualOptOutsFailure({
              error: error?.message || 'Unable to add opt-outs.',
            }))
          )
        )
      )
    )
  );

  startUsubFileValidation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CampaignActions.startUsubFileValidation),
      switchMap(({ listType, file, mappings, commType }) =>
        this.importOptoutsModalService.validateFile({
          fileName: file.name,
          mappings,
          contentType: file.type,
          originalFileSize: file.size,
          commType,
        }).pipe(
          switchMap((result: any) => {
            if (!result?.isActionSuccess) {
              return [CampaignActions.startUsubFileValidationFailure({ error: 'Unable to validate file.' })];
            }

            if (result?.isAlreadyExist) {
              return [CampaignActions.startUsubFileValidationFailure({
                error: `It looks like ${file.name} has already been uploaded. Kindly select another name for your file.`,
              })];
            }

            const fileId = result?.data?.fileId;
            const fileNameWithId = this.getFileNameWithId(file.name, fileId);
            const renamedFile = new File([file], fileNameWithId, { type: file.type });

            return [
              CampaignActions.startUsubFileValidationSuccess({
                fileId,
                fileName: file.name,
                fileNameWithId,
              }),
              CampaignActions.requestUsubSasUrl({
                file: renamedFile,
                fileNameWithId,
              }),
            ];
          }),
          catchError((error) =>
            of(CampaignActions.startUsubFileValidationFailure({
              error: error?.message || 'Unable to validate file.',
            }))
          )
        )
      )
    )
  );
  syncUsubImportFileIdToService$ = createEffect(
    () => this.actions$.pipe(
      ofType(CampaignActions.startUsubFileValidationSuccess),
      map(({ fileId }) => this.importOptoutsModalService.fileId.next(fileId))
    ),
    { dispatch: false }
  );
  requestUsubSasUrl$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CampaignActions.requestUsubSasUrl),
      switchMap(({ file, fileNameWithId }) =>
        this.importOptoutsModalService.getSasUrl().pipe(
          switchMap((result: any) => {
            if (!result?.isActionSuccess) {
              return [CampaignActions.requestUsubSasUrlFailure({ error: 'Unable to fetch upload credentials.' })];
            }

            return [
              CampaignActions.requestUsubSasUrlSuccess({
                file,
                fileNameWithId,
                sasUrl: result.data.sasUrl,
                containerName: result.data.containerName,
                sasToken: result.data.sasToken,
              }),
              CampaignActions.startUsubBlobUpload({
                file,
                fileNameWithId,
                sasUrl: result.data.sasUrl,
                containerName: result.data.containerName,
                sasToken: result.data.sasToken,
              }),
            ];
          }),
          catchError((error) =>
            of(CampaignActions.requestUsubSasUrlFailure({
              error: error?.message || 'Unable to fetch upload credentials.',
            }))
          )
        )
      )
    )
  );

  startUsubBlobUpload$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CampaignActions.startUsubBlobUpload),
      switchMap(({ file, fileNameWithId, sasUrl, containerName, sasToken }) =>
        this.importOptoutsModalService.sendFileToAzureBlob(sasUrl, containerName, sasToken, file).pipe(
          switchMap(() => [
            CampaignActions.startUsubBlobUploadSuccess({ fileNameWithId }),
            CampaignActions.startUsubHeartbeat({ fileNameWithId }),
            CampaignActions.startUsubValidationSse({ fileNameWithId }),
          ]),
          catchError((error) =>
            of(CampaignActions.startUsubBlobUploadFailure({
              error: error?.message || 'Unable to upload file.',
            }))
          )
        )
      )
    )
  );



  startUsubHeartbeat$ = createEffect(
    () => this.actions$.pipe(
      ofType(CampaignActions.startUsubHeartbeat),
      map(({ fileNameWithId }) => this.importOptoutsModalService.startHeartbeat(fileNameWithId))
    ),
    { dispatch: false }
  );

  stopUsubHeartbeat$ = createEffect(
    () => this.actions$.pipe(
      ofType(CampaignActions.stopUsubHeartbeat, CampaignActions.resetUsubImportState),
      map(() => this.importOptoutsModalService.stopHeartbeat())
    ),
    { dispatch: false }
  );

  clearUsubImportServiceFileId$ = createEffect(
    () => this.actions$.pipe(
      ofType(CampaignActions.resetUsubImportState),
      map(() => this.importOptoutsModalService.fileId.next(null))
    ),
    { dispatch: false }
  );

  startUsubValidationSse$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CampaignActions.startUsubValidationSse),
      switchMap(({ fileNameWithId }) =>
        this.importOptoutsModalService.getValidationSSE(fileNameWithId, 'validation').pipe(
          map((event) => {
            const status = event?.status ? String(event.status).toUpperCase() : '';
            if (status === 'COMPLETED') {
              return CampaignActions.usubValidationSseCompleted();
            }
            return CampaignActions.usubValidationSseStatus({ status });
          }),
          catchError((error) =>
            of(CampaignActions.usubValidationSseFailed({
              error: error?.message || 'Validation stream failed.',
            }))
          )
        )
      )
    )
  );

  loadUsubSummaryAfterValidation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CampaignActions.usubValidationSseCompleted),
      withLatestFrom(this.store.select(selectUsubImportState)),
      switchMap(([_, usubImport]) => {
        if (!usubImport.fileId) {
          return [CampaignActions.loadUsubImportSummaryFailure({ error: 'Missing file id for summary.' })];
        }
        return [CampaignActions.loadUsubImportSummary({ fileId: usubImport.fileId })];
      })
    )
  );

  loadUsubImportSummary$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CampaignActions.loadUsubImportSummary),
      switchMap(({ fileId }) =>
        this.importOptoutsModalService.getSummarizedReport(fileId).pipe(
          map((result: any) => {
            if (!result?.isActionSuccess) {
              return CampaignActions.loadUsubImportSummaryFailure({ error: 'Unable to fetch summarized report.' });
            }

            const recordsData = {
              totalRows: result.data.totalRows,
              invalidRows: result.data.invalidRows,
              duplicateRows: result.data.duplicateRows,
              uniqueEmailCount: result.data.uniqueEmailCount,
              uniquePhoneNumberCount: result.data.uniquePhoneNumberCount,
              totalFinalRecords: result.data.totalRows - (result.data.duplicateRows + result.data.invalidRows),
              actualValidRows: result.data.actualValidRows ?? null,
            };

            return CampaignActions.loadUsubImportSummarySuccess({ recordsData });
          }),
          catchError((error) =>
            of(CampaignActions.loadUsubImportSummaryFailure({
              error: error?.message || 'Unable to fetch summarized report.',
            }))
          )
        )
      )
    )
  );

  confirmUsubFileImport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CampaignActions.confirmUsubFileImport),
      withLatestFrom(this.store.select(selectUsubImportState)),
      switchMap(([{ fileName, listType }, usubImport]) => {
        const fileNameWithId = usubImport.fileNameWithId || fileName;
        return this.importOptoutsModalService.onFileUploadConfirmation(fileNameWithId).pipe(
          switchMap((result: any) => {
            if (!result?.isActionSuccess) {
              return [CampaignActions.confirmUsubFileImportFailure({ error: 'Unable to start file upload.' })];
            }
            return [
              CampaignActions.confirmUsubFileImportSuccess({ fileName, listType }),
              CampaignActions.startUsubHeartbeat({ fileNameWithId }),
              CampaignActions.startUsubUploadSse({ fileName: fileNameWithId, listType }),
            ];
          }),
          catchError((error) =>
            of(CampaignActions.confirmUsubFileImportFailure({
              error: error?.message || 'Unable to start file upload.',
            }))
          )
        );
      })
    )
  );

  startUsubUploadSse$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CampaignActions.startUsubUploadSse),
      switchMap(({ fileName, listType }) =>
        this.importOptoutsModalService.getValidationSSE(fileName, 'upload').pipe(
          map((event) => {
            const status = event?.status ? String(event.status).toUpperCase() : '';
            if (status === 'UPLOADED') {
              const actualValidRows = event?.actualValidRows != null ? Number(event.actualValidRows) : null;
              return CampaignActions.usubUploadSseCompleted({ listType, actualValidRows });
            }
            return CampaignActions.usubUploadSseStatus({ status });
          }),
          catchError((error) =>
            of(CampaignActions.usubUploadSseFailed({
              error: error?.message || 'Upload stream failed.',
            }))
          )
        )
      )
    )
  );

  completeUsubImport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CampaignActions.usubUploadSseCompleted),
      switchMap(({ listType, actualValidRows }) => [
        CampaignActions.stopUsubHeartbeat(),
        CampaignActions.usubImportCompleted({ listType, message: 'File uploaded Successfully', actualValidRows }),
      ])
    )
  );


  resubscribeContact$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CampaignActions.resubscribeContact),
      switchMap(({ listType, id }) =>
        this.unsubscribeService.reSubscribe(listType, id).pipe(
          map((result: { isActionSuccess: boolean; message: string }) => {
            if (result?.isActionSuccess) {
              return CampaignActions.resubscribeContactSuccess({
                listType,
                message: result.message || 'Unsubscribe Successfully Removed.',
              });
            }
            return CampaignActions.resubscribeContactFailure({
              error: 'Unable to resubscribe contact.',
            });
          }),
          catchError((error) =>
            of(CampaignActions.resubscribeContactFailure({
              error: error?.message || 'Unable to resubscribe contact.',
            }))
          )
        )
      )
    )
  );

  reloadAfterResubscribe$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CampaignActions.resubscribeContactSuccess),
      withLatestFrom(this.store.select(selectUnsubscribeState)),
      map(([action, unsubscribe]) => CampaignActions.loadUnsubscribeList({
        listType: action.listType,
        page: action.listType === 'email' ? unsubscribe.emailPage : unsubscribe.smsPage,
      }))
    )
  );

  reloadOnUnsubscribeTabChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CampaignActions.setUnsubscribeActiveTab),
      withLatestFrom(this.store.select(selectUnsubscribeState)),
      map(([action, unsubscribe]) => CampaignActions.loadUnsubscribeList({
        listType: action.tab,
        page: action.tab === 'email' ? unsubscribe.emailPage : unsubscribe.smsPage,
      }))
    )
  );
}
